import React from 'react'

interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  text?: string
  className?: string
  variant?: 'default' | 'gradient' | 'dots' | 'pulse' | 'orbit'
  color?: 'indigo' | 'emerald' | 'rose' | 'amber' | 'slate'
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  text,
  className = '',
  variant = 'default',
  color = 'indigo'
}) => {
  const sizeClasses = {
    xs: 'w-4 h-4',
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-14 h-14',
    xl: 'w-20 h-20'
  }

  const textSizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg'
  }

  const colorClasses = {
    indigo: {
      border: 'border-indigo-200 border-t-indigo-600',
      gradient: 'from-indigo-500 via-purple-500 to-pink-500',
      dot: 'bg-indigo-500',
      pulse: 'bg-indigo-500',
      text: 'text-indigo-600'
    },
    emerald: {
      border: 'border-emerald-200 border-t-emerald-600',
      gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
      dot: 'bg-emerald-500',
      pulse: 'bg-emerald-500',
      text: 'text-emerald-600'
    },
    rose: {
      border: 'border-rose-200 border-t-rose-600',
      gradient: 'from-rose-500 via-pink-500 to-fuchsia-500',
      dot: 'bg-rose-500',
      pulse: 'bg-rose-500',
      text: 'text-rose-600'
    },
    amber: {
      border: 'border-amber-200 border-t-amber-600',
      gradient: 'from-amber-500 via-orange-500 to-red-500',
      dot: 'bg-amber-500',
      pulse: 'bg-amber-500',
      text: 'text-amber-600'
    },
    slate: {
      border: 'border-slate-200 border-t-slate-600',
      gradient: 'from-slate-500 via-slate-600 to-slate-700',
      dot: 'bg-slate-500',
      pulse: 'bg-slate-500',
      text: 'text-slate-600'
    }
  }

  const colors = colorClasses[color]

  // 默认旋转样式
  const DefaultSpinner = () => (
    <div
      className={`animate-spin rounded-full border-[3px] ${colors.border} ${sizeClasses[size]}`}
    />
  )

  // 渐变旋转样式
  const GradientSpinner = () => (
    <div className={`relative ${sizeClasses[size]}`}>
      <div
        className={`absolute inset-0 rounded-full bg-gradient-to-r ${colors.gradient} animate-spin`}
        style={{
          mask: 'radial-gradient(transparent 60%, black 62%)',
          WebkitMask: 'radial-gradient(transparent 60%, black 62%)'
        }}
      />
      <div className="absolute inset-[3px] rounded-full bg-white" />
    </div>
  )

  // 点状加载样式
  const DotsSpinner = () => {
    const dotSize = {
      xs: 'w-1 h-1',
      sm: 'w-1.5 h-1.5',
      md: 'w-2 h-2',
      lg: 'w-2.5 h-2.5',
      xl: 'w-3 h-3'
    }
    return (
      <div className="flex items-center gap-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`${dotSize[size]} ${colors.dot} rounded-full animate-bounce`}
            style={{ animationDelay: `${i * 0.15}s` }}
          />
        ))}
      </div>
    )
  }

  // 脉冲样式
  const PulseSpinner = () => (
    <div className={`relative ${sizeClasses[size]}`}>
      <div className={`absolute inset-0 ${colors.pulse} rounded-full animate-ping opacity-30`} />
      <div className={`absolute inset-2 ${colors.pulse} rounded-full animate-pulse opacity-60`} />
      <div className={`absolute inset-4 ${colors.pulse} rounded-full`} />
    </div>
  )

  // 轨道样式
  const OrbitSpinner = () => (
    <div className={`relative ${sizeClasses[size]}`}>
      <div className="absolute inset-0 border-2 border-slate-200 rounded-full" />
      <div
        className={`absolute w-2 h-2 ${colors.dot} rounded-full animate-spin`}
        style={{
          top: '0',
          left: '50%',
          marginLeft: '-4px',
          marginTop: '-4px',
          transformOrigin: '4px calc(50% + 4px)'
        }}
      />
    </div>
  )

  const spinnerMap = {
    default: DefaultSpinner,
    gradient: GradientSpinner,
    dots: DotsSpinner,
    pulse: PulseSpinner,
    orbit: OrbitSpinner
  }

  const SpinnerComponent = spinnerMap[variant]

  return (
    <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
      <SpinnerComponent />
      {text && (
        <p className={`${textSizeClasses[size]} ${colors.text} font-medium animate-pulse`}>
          {text}
        </p>
      )}
    </div>
  )
}

// 全屏加载组件
export const FullPageSpinner: React.FC<{ message?: string }> = ({
  message = '加载中...'
}) => (
  <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full blur-xl opacity-30 animate-pulse" />
        <LoadingSpinner size="xl" variant="gradient" />
      </div>
      <p className="text-slate-600 font-medium">{message}</p>
    </div>
  </div>
)

// 按钮内加载组件
export const ButtonSpinner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <LoadingSpinner size="xs" variant="default" className={className} color="slate" />
)

// 内联加载组件
export const InlineSpinner: React.FC<{ text?: string }> = ({ text }) => (
  <span className="inline-flex items-center gap-2">
    <LoadingSpinner size="xs" variant="dots" />
    {text && <span className="text-sm text-slate-500">{text}</span>}
  </span>
)

export default LoadingSpinner
